import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, Snowflake, Flame, Home as HomeIcon, Building2, Wind,
  CheckCircle2, ArrowRight, Mountain,
} from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const SERVICES = [
  {
    id: "ac",
    icon: Snowflake,
    accent: "#00A8E1",
    label: "AC Repair & Installation",
    tag: "Cooling",
    headline: "Cold Air, On Command.",
    copy: "Central Valley summers don't negotiate, and neither do we. From capacitor swaps to full high-efficiency system installs, every job starts with real diagnostics and a load calculation — never a guess.",
    list: [
      "Same-day AC repair across the 60-mile radius",
      "Full system replacement with proper load sizing",
      "High-efficiency upgrades that cut summer bills",
      "Refrigerant leak detection & sealed-system repair",
      "Thermostat & smart-control installation",
    ],
    cta: "Beat the heat — book cooling service",
  },
  {
    id: "heating",
    icon: Flame,
    accent: "#E52425",
    label: "Heating & Furnace Systems",
    tag: "Heating",
    headline: "Warmth Without Worry.",
    copy: "Foggy Valley winters demand a furnace that fires every time. We repair all major brands and install right-sized heating systems with safety checks that go beyond the minimum.",
    list: [
      "Furnace repair — all major brands",
      "Full furnace & heat-pump replacement",
      "Carbon-monoxide & heat-exchanger safety inspections",
      "Ignition, blower & control-board diagnostics",
      "Duct sealing for even, efficient heat",
    ],
    cta: "Get winter-ready — book heating service",
  },
  {
    id: "mobile",
    icon: HomeIcon,
    accent: "#A1784E",
    label: "Mobile Home HVAC Solutions",
    tag: "Niche Authority",
    featured: true,
    headline: "Mobile Homes Are Our Home Turf.",
    copy: "Mobile home HVAC runs on its own equipment class, its own ductwork geometry, and tighter mechanical clearances. We specialize in all of it — mobile home AC & heating repair, full system replacement, crossover ductwork, and the specialized maintenance these systems need to survive Valley summers.",
    list: [
      "Mobile home AC & heating repair — all major brands",
      "Complete mobile home system replacement, right-sized",
      "Crossover & under-home duct repair, sealing, replacement",
      "Specialized seasonal maintenance for mobile homes",
      "Park-friendly scheduling & clear documentation",
    ],
    cta: "Talk to a mobile-home specialist",
  },
  {
    id: "commercial",
    icon: Building2,
    accent: "#00A8E1",
    label: "Light Commercial",
    tag: "Commercial",
    headline: "Comfort That Keeps You Open.",
    copy: "Shops, offices, and small facilities run on their HVAC. We maintain and install light-commercial systems with minimal downtime, clear reporting, and planned maintenance that prevents the Friday-afternoon failure.",
    list: [
      "Rooftop package-unit service & replacement",
      "Planned maintenance programs with reporting",
      "Split systems for offices & retail",
      "Priority dispatch for commercial clients",
      "Ventilation & make-up-air solutions",
    ],
    cta: "Protect your uptime — book commercial service",
  },
  {
    id: "tuneups",
    icon: Wind,
    accent: "#676B6C",
    label: "Tune-Ups & Air Quality",
    tag: "Prevention",
    headline: "Cheaper To Keep Than To Fix.",
    copy: "The most expensive repair is the one a tune-up would have caught. Seasonal maintenance plus air-quality upgrades that make Valley dust, pollen, and wildfire smoke someone else's problem.",
    list: [
      "Seasonal AC & furnace tune-ups",
      "Filter programs & high-MERV upgrades",
      "Whole-home air purification & UV options",
      "Humidity control for Valley winters",
      "Duct inspection & airflow balancing",
    ],
    cta: "Book a tune-up before the season turns",
  },
];

const Services = () => {
  const [active, setActive] = useState("mobile");
  const current = SERVICES.find((s) => s.id === active);

  return (
    <>
      <section data-testid="services-hero" className="relative overflow-hidden bg-bear-ink py-24 text-white lg:py-32">
        <div className="pointer-events-none absolute -right-8 top-10 select-none font-display text-[24vw] leading-none text-stroke-ghost lg:text-[16rem]">
          TOOLS
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Mountain className="h-4 w-4 text-bear-gold" />
            <span className="font-head text-xs font-bold uppercase tracking-[0.32em] text-bear-gold">
              Services
            </span>
          </div>
          <h1 className="mt-6 font-display uppercase leading-[0.92] tracking-wide">
            <MaskedLines
              delay={0.15}
              lineClassName="text-[clamp(2.8rem,8.5vw,7.5rem)]"
              lines={[
                <span key="a">Every System.</span>,
                <span key="b" className="text-bear-cyan">Every Season.</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">
              Five disciplines, one standard: diagnosed honestly, priced upfront,
              fixed right. Pick your system below.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="services-tabs-section" className="bg-bear-paper py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[380px_1fr]">
          <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
            {SERVICES.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                data-testid={`service-tab-${s.id}`}
                className={`group relative flex min-w-[240px] items-center gap-4 rounded-md border p-5 text-left transition-all duration-300 lg:min-w-0 ${
                  active === s.id
                    ? "border-transparent bg-bear-ink text-white shadow-[0_16px_40px_rgba(20,23,25,0.25)]"
                    : "border-black/10 bg-white hover:border-bear-cyan/50"
                }`}
              >
                <span
                  className="font-display text-2xl tracking-wide"
                  style={{ color: active === s.id ? s.accent : "rgba(20,23,25,0.15)" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1">
                  <span className={`block font-head text-sm font-bold leading-snug ${active === s.id ? "text-white" : "text-bear-ink"}`}>
                    {s.label}
                  </span>
                  <span className={`mt-0.5 block text-xs ${active === s.id ? "text-white/50" : "text-bear-slate"}`}>
                    {s.tag}
                  </span>
                </span>
                <s.icon className="h-5 w-5 shrink-0" style={{ color: s.accent }} />
                {s.featured && (
                  <span className="absolute -top-2 right-3 rounded-full bg-bear-gold px-2.5 py-0.5 font-head text-[9px] font-bold uppercase tracking-[0.12em] text-bear-ink">
                    Specialty
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="relative min-h-[520px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: EASE }}
                data-testid={`service-panel-${current.id}`}
                className="overflow-hidden rounded-md border border-black/10 bg-white shadow-[0_24px_60px_rgba(20,23,25,0.08)]"
              >
                <div className="h-1.5 w-full" style={{ background: current.accent }} />
                <div className="p-8 sm:p-12">
                  <div className="flex items-center gap-4">
                    <span
                      className="flex h-14 w-14 items-center justify-center rounded-md"
                      style={{ background: `${current.accent}1A` }}
                    >
                      <current.icon className="h-7 w-7" style={{ color: current.accent }} />
                    </span>
                    <div>
                      <p className="font-head text-[11px] font-bold uppercase tracking-[0.24em]" style={{ color: current.accent }}>
                        {current.tag}
                      </p>
                      <h2 className="font-head text-xl font-bold text-bear-ink sm:text-2xl">{current.label}</h2>
                    </div>
                  </div>
                  <p className="mt-8 font-display text-3xl uppercase leading-tight tracking-wide text-bear-ink sm:text-4xl">
                    {current.headline}
                  </p>
                  <p className="mt-5 max-w-2xl text-base leading-relaxed text-bear-slate">{current.copy}</p>
                  <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                    {current.list.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-bear-ink">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: current.accent }} />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-black/10 pt-8">
                    <a
                      href={PHONE_TEL}
                      data-testid={`service-call-cta-${current.id}`}
                      className="flex items-center gap-3 rounded-full bg-bear-red px-7 py-3.5 font-head text-sm font-bold text-white transition-transform hover:scale-105"
                    >
                      <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                    </a>
                    <Link
                      to="/contact"
                      data-testid={`service-book-cta-${current.id}`}
                      className="group flex items-center gap-2 font-head text-sm font-bold uppercase tracking-wider text-bear-ink"
                    >
                      {current.cta}
                      <ArrowRight className="h-4 w-4 text-bear-cyan transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section className="bg-bear-ink py-20 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-4xl uppercase tracking-wide sm:text-5xl">
              Not Sure What <span className="text-bear-cyan">You Need?</span>
            </h2>
            <p className="mt-3 max-w-xl text-white/60">
              Describe the symptom — we'll diagnose the system. One call, one
              honest answer, one flat price before work starts.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={PHONE_TEL}
              data-testid="services-bottom-call-cta"
              className="flex items-center gap-3 rounded-full bg-bear-red px-8 py-4 font-head text-base font-bold text-white transition-transform hover:scale-105"
            >
              <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Services;
